import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ClipboardList, Volume2, Loader2, Square, ArrowRight, Ear, HeartHandshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchSceneAudio } from "@/lib/api";
import { trackAudio, CONTENT_NOTES, TOPIC_NOTES } from "@/lib/audio";

function briefingIcon(audioState) {
  if (audioState === "loading") return <Loader2 className="h-4 w-4 animate-spin" />;
  if (audioState === "playing") return <Square className="h-3.5 w-3.5" />;
  return <Volume2 className="h-4 w-4" />;
}

function briefingLabel(audioState) {
  if (audioState === "playing") return "Stop";
  if (audioState === "loading") return "Preparing…";
  return "Hear the briefing";
}

export const BriefingCard = ({ scenario, autoNarrate, onStart }) => {
  const [audioState, setAudioState] = useState("idle");
  const audioRef = useRef(null);

  const play = async () => {
    setAudioState("loading");
    try {
      const url = await fetchSceneAudio(scenario.id, "briefing");
      const a = trackAudio(new Audio(url));
      audioRef.current = a;
      a.onended = () => setAudioState("idle");
      await a.play();
      setAudioState("playing");
    } catch {
      setAudioState("idle");
    }
  };

  const toggle = () => {
    if (audioState === "playing") {
      audioRef.current?.pause();
      audioRef.current = null;
      setAudioState("idle");
      return;
    }
    if (audioState !== "loading") play();
  };

  useEffect(() => {
    if (autoNarrate) play();
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-stone-100 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        data-testid="briefing-card"
        className="w-full max-w-2xl rounded-2xl border border-stone-200 bg-white p-8 sm:p-10 shadow-sm"
      >
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-stone-500">
          <ClipboardList className="h-4 w-4" />
          Pre-shift briefing
        </div>
        <h2 className="font-heading mt-3 text-2xl sm:text-3xl font-semibold text-stone-900">
          {scenario.title}
        </h2>
        <p className="mt-1 text-sm text-stone-500">
          Your supervisor pulls you aside before you begin.
        </p>

        <p data-testid="briefing-text" className="mt-6 rounded-xl border-l-2 border-emerald-500 bg-emerald-50/50 px-5 py-4 text-base leading-relaxed text-stone-700 italic">
          {scenario.briefing}
        </p>

        {TOPIC_NOTES[scenario.id] && (
          <div data-testid="topic-note" className="mt-4 flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50/60 px-4 py-3">
            <HeartHandshake className="h-4 w-4 text-rose-700 mt-0.5 shrink-0" />
            <p className="text-sm leading-relaxed text-rose-900">
              {TOPIC_NOTES[scenario.id]} Take what you need; you can step away at any time.
            </p>
          </div>
        )}

        {CONTENT_NOTES[scenario.id] && (
          <div data-testid="content-note" className="mt-4 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3">
            <Ear className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
            <p className="text-sm leading-relaxed text-amber-900">
              {CONTENT_NOTES[scenario.id]} You can mute audio at any time.
            </p>
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <button
            data-testid="play-briefing-button"
            onClick={toggle}
            className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-stone-50 px-4 py-2 text-sm font-medium text-stone-600 hover:border-emerald-500 hover:text-emerald-800 transition-colors duration-200"
          >
            {briefingIcon(audioState)}
            {briefingLabel(audioState)}
          </button>
          <Button
            data-testid="start-shift-button"
            onClick={onStart}
            className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-6"
          >
            Start the shift <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      </motion.div>
    </div>
  );
};
